import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FaPlusCircle } from "react-icons/fa";
import { BiBarcodeReader } from "react-icons/bi";
import { PiListNumbersFill } from "react-icons/pi";
import ReactModal from "react-modal";
import Swal from "sweetalert2";

import Container from "../../components/Container";
import { Loading } from "../../../../components/Loading";
import { PackageCard } from "../../components/PackageCard";
import { BrComerceApiService } from "../../../../services/BrComerceApiService";
import { barcodeImage } from "../../../../utils/brcodeImage";
import useGetWindowWidth from "../../../../utils/useGetWindowWidth";
import barCodeScanner from "../../../../assets/images/Barcode-amico.svg";
import styles from "./styles.module.scss";

const modalStyle = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(51, 51, 51, 0.75)",
    zIndex: 999
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    zIndex: 999,
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    overflow: "visible"
  }
};

const BELT_FIXED_PARAMS = {
  serial: "123",
  multiplo: "1",
  auth: "74e9c74a-d23c-4677-bf8e-088b62e9c228",
  tipo: "1"
};

export const BeltValidation = () => {
  const api = new BrComerceApiService();
  const bufferRef = useRef("");
  const widthWindows = useGetWindowWidth();

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    window.setPageTitle("Validação de Esteira");
  }, []);

  const [measures, setMeasures] = useState({
    peso: "",
    altura: "",
    largura: "",
    comprimento: ""
  });

  const [shippings, setShippings] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");

  const [addPackageModal, setAddPackageModal] = useState(false);
  const [scannerModal, setScannerModal] = useState(false);
  const [manualModal, setManualModal] = useState(false);

  const [sroOk, setSroOk] = useState("vazio");
  const [loadingSro, setLoadingSro] = useState(false);
  const [sroInput, setSroInput] = useState({ code: "", error: "", isOK: "vazio" });

  const filteredShippings = useMemo(() => {
    if (!searchFilter) return shippings;
    const term = searchFilter.toLowerCase();
    return shippings.filter(s => {
      const sro = (s.sro || s.name || "").toLowerCase();
      const clientName = s.user?.userData?.[1]?.Value?.toLowerCase() || "";
      const destinationName = s.destination?.name?.toLowerCase() || "";
      return sro.includes(term) || clientName.includes(term) || destinationName.includes(term);
    });
  }, [shippings, searchFilter]);

  const handleMeasureChange = useCallback((e) => {
    const { name, value } = e.target;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setMeasures(prev => ({ ...prev, [name]: value }));
    }
  }, []);

  const resetSroState = useCallback(() => {
    setSroInput({ code: "", error: "", isOK: "vazio" });
    setSroOk("vazio");
  }, []);

  const searchShippingBySro = useCallback(async (code) => {
    if (!code) {
      setSroInput(prev => ({ ...prev, error: "Etiqueta é obrigatório", isOK: "notOk" }));
      setLoadingSro(false);
      return;
    }

    const alreadyExists = shippings.some(
      s => s.sro?.toLowerCase() === code.toLowerCase()
    );

    if (alreadyExists) {
      setSroInput(prev => ({ ...prev, error: "Etiqueta já adicionado na lista", isOK: "notOk" }));
      setLoadingSro(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.makeHttpRequest({
        method: "GET",
        url: `/shipping/findBySro/${code}`
      });

      if (!response) {
        setSroInput(prev => ({ ...prev, error: "Shipping não encontrado", isOK: "notOk" }));
      } else {
        setShippings(prev => [response, ...prev]);
        resetSroState();
      }
    } catch (err) {
      const message = err?.response?.data?.message ?? "Falha ao buscar shipping";
      setSroInput(prev => ({ ...prev, error: message, isOK: "notOk" }));
    } finally {
      setLoading(false);
      setLoadingSro(false);
    }
  }, [shippings, resetSroState]);

  const handleManualSearch = useCallback(() => {
    setLoadingSro(true);
  }, []);

  const handleInputCode = useCallback((e) => {
    const value = e.target.value?.trim();
    setSroInput({
      code: value,
      error: !value ? "Campo obrigatório" : "",
      isOK: !value ? "notOk" : "vazio"
    });
  }, []);

  const handleRemoveShipping = useCallback((shippingId) => {
    setShippings(prev => prev.filter(s => s.id !== shippingId));
  }, []);

  const clearAll = useCallback(() => {
    setShippings([]);
    setMeasures({ peso: "", altura: "", largura: "", comprimento: "" });
    setSearchFilter("");
  }, []);

  const validateShippings = useCallback(async () => {
    const { peso, altura, largura, comprimento } = measures;

    if (!peso || !altura || !largura || !comprimento) {
      Swal.fire({
        title: "Atenção",
        text: "Preencha todos os campos de medidas antes de validar",
        icon: "warning"
      });
      return;
    }

    if (shippings.length === 0) {
      Swal.fire({
        title: "Atenção",
        text: "Adicione pelo menos um shipping para validar",
        icon: "warning"
      });
      return;
    }

    setValidating(true);
    let successCount = 0;
    let errorCount = 0;

    for (const shipping of shippings) {
      const sro = shipping.sro || shipping.name;
      try {
        await api.makeHttpRequest({
          method: "GET",
          url: "/shipping/send-to-belt-validation",
          params: {
            ...BELT_FIXED_PARAMS,
            etiqueta: sro,
            peso,
            altura,
            largura,
            comprimento
          }
        });
        successCount++;
      } catch {
        errorCount++;
      }
    }

    setValidating(false);

    if (errorCount === 0) {
      Swal.fire({
        icon: "success",
        title: "Sucesso",
        text: `${successCount} shipping(s) validado(s) com sucesso!`,
        confirmButtonText: "Ok"
      }).then(() => {
        clearAll();
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: `${successCount} sucesso(s), ${errorCount} erro(s).`
      });
    }
  }, [measures, shippings, clearAll]);

  // Scanner keypress listener
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (scannerModal) {
        if (event.key === "Enter") {
          if (bufferRef.current) {
            setSroInput({ code: bufferRef.current, error: "", isOK: "vazio" });
            setLoadingSro(true);
            setSroOk("ok");
            bufferRef.current = "";
          }
        } else {
          bufferRef.current += event.key;
        }
      }
    };

    window.addEventListener("keypress", handleKeyPress);
    return () => window.removeEventListener("keypress", handleKeyPress);
  }, [scannerModal]);

  useEffect(() => {
    if (loadingSro) {
      searchShippingBySro(sroInput.code);
    }
  }, [loadingSro]);

  const measuresValid = measures.peso && measures.altura && measures.largura && measures.comprimento;

  return (
    <>
      <Loading loading={loading || validating} />

      {/* Modal: Escolher modo */}
      <ReactModal
        isOpen={addPackageModal}
        shouldCloseOnOverlayClick={true}
        onRequestClose={() => setAddPackageModal(false)}
        style={modalStyle}
      >
        <div className={styles.modalBody}>
          <h6>Adicionar pacotes</h6>
          <span>Selecione uma das opções abaixo para prosseguir:</span>
          <div className={styles.buttonContainerAddPackageModal}>
            <button
              className={styles.scanner}
              onClick={() => {
                setAddPackageModal(false);
                setScannerModal(true);
              }}
            >
              <BiBarcodeReader color="#fff" size={24} />
              Escanear a etiqueta do pacote
            </button>
          </div>
          <div className={styles.buttonContainerAddPackageModal}>
            <button
              className={styles.digitCode}
              onClick={() => {
                setAddPackageModal(false);
                setManualModal(true);
              }}
            >
              <PiListNumbersFill color="#fff" size={20} />
              Digitar código do pacote manualmente
            </button>
          </div>
        </div>
      </ReactModal>

      {/* Modal: Scanner (não fecha ao bipar) */}
      <ReactModal
        isOpen={scannerModal}
        shouldCloseOnOverlayClick={true}
        onRequestClose={() => {
          setScannerModal(false);
          resetSroState();
        }}
        shouldCloseOnEsc
        style={modalStyle}
      >
        <div className={styles.modalBody2}>
          <div>
            <h6>Escanear etiqueta</h6>
            <p>Por favor, mantenha o bip continuamente apontado para a etiqueta</p>
            <div className={styles.imgBarcode} role={sroOk}>
              <img src={barCodeScanner} alt="barcode scanner" />
            </div>
          </div>
          <div className={styles.scanInputBox} role={sroOk}>
            <input type="text" style={{ display: "none" }} />
            {sroInput.code && (
              <div className={styles.tracking}>
                <img src={`data:image/jpeg;base64,${barcodeImage}`} alt="Código de Barras" />
                <span>{sroInput.code}</span>
              </div>
            )}
          </div>
          {sroInput.isOK === "notOk" && (
            <p className={styles.error}>{sroInput.error}</p>
          )}
          {shippings.length > 0 && (
            <p className={styles.scannedCount}>{shippings.length} pacote(s) escaneado(s)</p>
          )}
        </div>
      </ReactModal>

      {/* Modal: Manual */}
      <ReactModal
        isOpen={manualModal}
        shouldCloseOnOverlayClick={true}
        onRequestClose={() => {
          setManualModal(false);
          resetSroState();
        }}
        shouldCloseOnEsc
        style={modalStyle}
      >
        <div className={styles.modalBody}>
          <h6>Digitar código manualmente</h6>
          <span>
            Preencha o campo abaixo com o código <br />
            do pacote que deseja adicionar
          </span>
          <div className={styles.input}>
            <input
              placeholder="Digite o código aqui"
              type="text"
              onChange={handleInputCode}
              value={sroInput.code}
              name="code"
            />
          </div>
          {sroInput.isOK === "notOk" && (
            <p className={styles.error}>{sroInput.error}</p>
          )}
          <div className={styles.buttonContainerModal}>
            <button
              onClick={() => {
                setManualModal(false);
                setAddPackageModal(true);
                resetSroState();
              }}
            >
              Voltar
            </button>
            <button onClick={handleManualSearch}>Confirmar</button>
          </div>
        </div>
      </ReactModal>

      <Container title="Validação de Esteira">
        <p className={styles.title}>Preencha as medidas e bipe os pacotes que deseja validar:</p>

        <Row className={styles.row}>
          <Col>
            <p className={styles.textSection}>Medidas e Peso</p>
          </Col>
        </Row>
        <Row className={styles.row}>
          <Col>
            <div className={styles.input}>
              <input
                type="text"
                name="peso"
                placeholder="Peso (kg)"
                value={measures.peso}
                onChange={handleMeasureChange}
              />
            </div>
          </Col>
          <Col>
            <div className={styles.input}>
              <input
                type="text"
                name="altura"
                placeholder="Altura (cm)"
                value={measures.altura}
                onChange={handleMeasureChange}
              />
            </div>
          </Col>
          <Col>
            <div className={styles.input}>
              <input
                type="text"
                name="largura"
                placeholder="Largura (cm)"
                value={measures.largura}
                onChange={handleMeasureChange}
              />
            </div>
          </Col>
          <Col>
            <div className={styles.input}>
              <input
                type="text"
                name="comprimento"
                placeholder="Comprimento (cm)"
                value={measures.comprimento}
                onChange={handleMeasureChange}
              />
            </div>
          </Col>
        </Row>

        <Row className={styles.row}>
          <Col>
            <p className={styles.textSection}>
              {`Pacotes ${shippings.length ? `(${String(shippings.length).padStart(2, '0')})` : ""}`}
            </p>
          </Col>
        </Row>

        <div className={styles.actionsButton}>
          <button
            type="button"
            onClick={() => setAddPackageModal(true)}
            className={styles.package}
            disabled={loading || validating}
          >
            <FaPlusCircle size={16} color="#FFFFFF" />
            <span>Adicionar pacotes</span>
          </button>
        </div>

        {!!shippings.length && (
          <div className={styles.packageContainer}>
            <Row className={styles.row}>
              <Col className={styles.searchCol}>
                <div className={styles.input}>
                  <input
                    type="text"
                    placeholder="Pesquisar por Etiqueta, nome ou cliente..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                  />
                </div>
              </Col>
            </Row>
            <div className={styles.packagesWrapper}>
              {filteredShippings.map(shipping => (
                <PackageCard
                  key={shipping.id}
                  widthWindows={widthWindows}
                  shipping={shipping}
                  onDelete={() => handleRemoveShipping(shipping.id)}
                  expand={false}
                />
              ))}
              {filteredShippings.length === 0 && searchFilter && (
                <p className={styles.noResults}>Nenhum pacote encontrado para "{searchFilter}"</p>
              )}
            </div>
          </div>
        )}

        <div className={styles.buttonsContainer}>
          <button
            type="button"
            onClick={validateShippings}
            className={styles.create}
            disabled={!measuresValid || !shippings.length || validating}
          >
            <span>{validating ? "Validando..." : "Validar"}</span>
          </button>
        </div>
      </Container>
    </>
  );
};
